import React from "react";
//Limitless imports
import { onDownloadFormatter } from "../..";
import { useTranslation, useAppConfig } from "@limitless/common/hooks";
import { useManagerProfileContext } from "../../../manager/hooks";
export const INITIAL_OPTIONS = {
  selectableRows: "none",
  responsive: "standard",
  filter: true,
  print: false,
  search: true,
  elevation: 2,
  rowsPerPage: 20,
  rowsPerPageOptions: [20, 50, 100],
  textLabels: {
    body: {
      noMatch: "No records found"
    }
  },
  download: true,
  selectToolbarPlacement: "above"
};
const INITIAL_TABLE = {
  options: {},
  columns: {}
};
export const useTableState = () => {
  const [tables, setTables] = React.useState([]);
  const {
    getCurrencySigns
  } = useAppConfig();
  const {
    managerProfile
  } = useManagerProfileContext();
  const {
    t
  } = useTranslation();
  const getSortingOptions = ref => {
    return {
      onTableChange: (action, tableState) => handleTableChange(action, tableState, ref)
    };
  };
  const getDownloadFormattingOptions = options => {
    if (options.download) {
      return {
        onDownload: (buildHead, buildBody, columns, data) => onDownloadFormatter(buildHead, buildBody, columns, data)(columns, {
          getCurrencySigns,
          managerProfile
        })
      };
    }
    return {};
  };
  const getTable = id => {
    return tables.find(table => table.id === id);
  };
  const updateTable = table => {
    if (table !== null && table !== void 0 && table.id) {
      const ref = tables.find(t => t.id === table.id);
      if (ref) {
        let tablesCopy = tables;
        tablesCopy = tablesCopy.filter(t => t.id !== ref.id);
        tablesCopy.push(table);
        setTables(tablesCopy);
      } else {
        setTables([...tables, table]);
      }
    }
  };
  const handleTableChange = (action, tableState, ref) => {
    if (action === "sort") {
      ref.options.sortOrder = tableState.sortOrder;
      updateTable(ref);
    }
    if (action === "viewColumnsChange") {
      const displayOptions = tableState.columns.map(col => ({
        display: col.display
      }));
      ref.columns.displayOptions = {
        ...ref.columns,
        ...displayOptions
      };
      updateTable(ref);
    }
    if (action === "changeRowsPerPage") {
      ref.options.rowsPerPage = tableState.rowsPerPage;
      updateTable(ref);
    }
  };
  const initTable = ref => {
    ref.options = {
      ...ref.options,
      ...getSortingOptions(ref)
    };
    //This code is causing the console error
    updateTable(ref);
    return ref;
  };
  const getComponentOwnedOptions = (refOptions, componentOpions) => {
    //often the options can contains functions with a reference to some data which could have been updated
    //if we don't use the incoming functions from the options, the one stored in the context will have old data causing unexpected side effects
    //see TenantTable as an example.
    //they could also be owned by the component, i.e. pagination that is dynamic based on data in the table
    const COMPONENT_OWNED_KEYS = ["onRowClick", "onRowsDelete", "customToolbar", "customToolbarSelect", "downloadOptions", "customFooter", "onFilterChange", "onRowSelectionChange", "rowsSelected", "isRowSelectable", "selectableRows", "setRowProps", "pagination", "rowsPerPage", "filename", "textLabels"];
    const finalOptions = {
      ...refOptions,
      ...getDownloadFormattingOptions(refOptions)
    };
    COMPONENT_OWNED_KEYS.map(key => {
      if (componentOpions[key]) {
        finalOptions[key] = componentOpions[key];
      }
    });
    if (finalOptions.download && finalOptions.filename) {
      finalOptions.downloadOptions = {
        ...finalOptions.downloadOptions,
        filename: `${new Date().toISOString().split("T")[0]}-${finalOptions.filename}.csv`
      };
      Reflect.deleteProperty(finalOptions, "filename");
    }
    return finalOptions;
  };
  const getOptions = (id, columns, options, data, loading) => {
    let muiOptions = {
      ...INITIAL_OPTIONS,
      ...options
    };
    let muiColumns = columns;
    if (loading) {
      muiOptions = {
        ...muiOptions,
        textLabels: {
          body: {
            noMatch: "Loading..."
          }
        }
      };
    } else {
      var _muiOptions$textLabel, _muiOptions$textLabel2, _muiOptions$textLabel3, _muiOptions$textLabel4;
      muiOptions = {
        ...muiOptions,
        textLabels: {
          body: {
            noMatch: "No matches found",
            ...((_muiOptions$textLabel = (_muiOptions$textLabel2 = muiOptions.textLabels) === null || _muiOptions$textLabel2 === void 0 ? void 0 : _muiOptions$textLabel2.body) !== null && _muiOptions$textLabel !== void 0 ? _muiOptions$textLabel : {})
          },
          selectedRows: {
            text: "row(s) selected",
            ...((_muiOptions$textLabel3 = (_muiOptions$textLabel4 = muiOptions.textLabels) === null || _muiOptions$textLabel4 === void 0 ? void 0 : _muiOptions$textLabel4.selectedRows) !== null && _muiOptions$textLabel3 !== void 0 ? _muiOptions$textLabel3 : {})
          }
        }
      };
    }
    if (muiOptions.download && !muiOptions.onDownload) {
      //if the table doesn't have a custom onDownload, use the generic one
      muiOptions = {
        ...muiOptions,
        ...getDownloadFormattingOptions(muiOptions)
      };
    }
    if (id) {
      let ref = getTable(id);
      if (!ref) {
        // first instance of table, create new
        ref = {
          id,
          ...INITIAL_TABLE
        };
        ref.options = muiOptions;
        ref = initTable(ref);
      }
      muiOptions = getComponentOwnedOptions(ref.options, options);
      muiColumns = muiColumns.map((column, index) => {
        // Retrive display option
        const displayProp = ref.columns.displayOptions ? ref.columns.displayOptions[index] : {};
        return {
          ...column,
          options: {
            ...column.options,
            ...displayProp
          } // mutate display option
        };
      });
    }
    muiOptions = {
      ...muiOptions,
      textLabels: {
        ...muiOptions.textLabels,
        body: {
          ...muiOptions.textLabels.body,
          toolTip: t("Sort")
        },
        toolbar: {
          search: t("Search"),
          filterTable: t("Filter Table")
        },
        filter: {
          all: t("All"),
          title: t("FILTERS"),
          reset: t("RESET")
        }
      }
    };
    return {
      muiOptions,
      muiColumns
    };
  };
  return {
    tables,
    getOptions
  };
};